import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ModernNavbar from '../component/header/ModernNavbar';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const DemoPage = () => {
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');
  const cardBg = useColorModeValue('white', 'neutral.800');

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box>
      <ModernNavbar />
      
      {/* Hero Section */}
      <Box
        bgImage="url('/assets/images/singha/floor1/IMG_7829.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        py={20}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(36, 36, 36, 0.5)"
        />
        <Container maxW="1200px" position="relative" zIndex={2}>
          <MotionWrapper
           
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading
                as="h1"
                size="2xl"
                fontFamily="heading"
                fontWeight="normal"
              >
                เปรียบเทียบเวอร์ชัน
              </Heading>
              <Text fontSize="xl" maxW="800px" lineHeight="1.6">
                เปรียบเทียบระหว่างเว็บไซต์เดิมและเว็บไซต์ใหม่ของพิพิธภัณฑ์สิงห์ท่า
              </Text>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Comparison Section */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  เปรียบเทียบเวอร์ชัน
                </Heading>
                <Text fontSize="lg" color={textColor} maxW="800px">
                  ดูความแตกต่างระหว่างเว็บไซต์เดิมและเว็บไซต์ใหม่ที่ได้รับการออกแบบใหม่
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
                {/* Original Version */}
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                  textAlign="center"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color="neutral.600">
                    เวอร์ชันเดิม
                  </Heading>
                  <VStack spacing={4} align="flex-start" textAlign="left">
                    <Text color={textColor}>
                      • ออกแบบด้วย HTML/CSS แบบดั้งเดิม
                    </Text>
                    <Text color={textColor}>
                      • ใช้ Bootstrap framework
                    </Text>
                    <Text color={textColor}>
                      • การตอบสนองแบบพื้นฐาน
                    </Text>
                    <Text color={textColor}>
                      • ไม่มีแอนิเมชัน
                    </Text>
                    <Text color={textColor}>
                      • UI แบบคลาสสิก
                    </Text>
                  </VStack>
                  <Button
                    mt={6}
                    bg="neutral.500"
                    _hover={{ bg: 'neutral.600' }}
                    onClick={() => window.location.href = '/'}
                  >
                    ดูเวอร์ชันเดิม
                  </Button>
                </Box>

                {/* Modern Version */}
                <Box
                  bg={cardBg}
                  p={8}
                  borderRadius="lg"
                  boxShadow="lg"
                  textAlign="center"
                  border="2px solid"
                  borderColor="brand.500"
                >
                  <Heading as="h3" size="lg" mb={4} fontFamily="heading" color="brand.500">
                    เวอร์ชันใหม่
                  </Heading>
                  <VStack spacing={4} align="flex-start" textAlign="left">
                    <Text color={textColor}>
                      • ออกแบบด้วย React.js และ Chakra UI
                    </Text>
                    <Text color={textColor}>
                      • ใช้ Framer Motion สำหรับแอนิเมชัน
                    </Text>
                    <Text color={textColor}>
                      • การตอบสนองที่ทันสมัย
                    </Text>
                    <Text color={textColor}>
                      • แอนิเมชันที่สวยงาม
                    </Text>
                    <Text color={textColor}>
                      • UI แบบมินิมอลและทันสมัย
                    </Text>
                  </VStack>
                  <Button
                    mt={6}
                    bg="brand.500"
                    _hover={{ bg: 'brand.600' }}
                    onClick={() => window.location.href = '/modern'}
                  >
                    ดูเวอร์ชันใหม่
                  </Button>
                </Box>
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Features Comparison */}
      <Box py={20} bg="neutral.50">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color="neutral.900"
                >
                  คุณสมบัติใหม่
                </Heading>
                <Text fontSize="lg" color="neutral.700" maxW="800px">
                  คุณสมบัติใหม่ที่เพิ่มเข้ามาในเวอร์ชันใหม่
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    แอนิเมชัน
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    แอนิเมชันที่สวยงามด้วย Framer Motion 
                    ทำให้การใช้งานน่าสนใจมากขึ้น
                  </Text>
                </Box>

                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    การตอบสนอง
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    การตอบสนองที่รวดเร็วและลื่นไหล 
                    ด้วย React.js และ Chakra UI
                  </Text>
                </Box>

                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    UI/UX
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    การออกแบบที่ทันสมัยและใช้งานง่าย 
                    ด้วยหลักการ UX ที่ดี
                  </Text>
                </Box>

                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    Mobile First
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    ออกแบบให้รองรับอุปกรณ์มือถือเป็นหลัก 
                    และปรับตัวได้กับทุกขนาดหน้าจอ
                  </Text>
                </Box>

                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    Performance
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    ประสิทธิภาพที่ดีขึ้น 
                    ด้วยการ optimize และ lazy loading
                  </Text>
                </Box>

                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  textAlign="center"
                >
                  <Heading as="h3" size="md" mb={3} fontFamily="heading" color="brand.500">
                    Accessibility
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    การเข้าถึงที่ดีขึ้น 
                    รองรับผู้ใช้ที่มีความต้องการพิเศษ
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={20} bg="brand.500">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8} textAlign="center" color="white">
              <Heading
                as="h2"
                size="xl"
                fontFamily="heading"
              >
                เลือกเวอร์ชันที่คุณชอบ
              </Heading>
              <Text fontSize="lg" maxW="600px" opacity={0.9}>
                คุณสามารถเลือกใช้เวอร์ชันเดิมหรือเวอร์ชันใหม่ได้ตามความต้องการ
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="brand.500"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => window.location.href = '/'}
                >
                  ใช้เวอร์ชันเดิม
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  onClick={() => window.location.href = '/modern'}
                >
                  ใช้เวอร์ชันใหม่
                </Button>
              </HStack>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default DemoPage; 